import { useState } from "react";
import { Token } from "../pages/RemodelingResume/types";
import { createContext, useContext } from "react";

import axios from "axios";
import jwt from "jwt-decode";
import {
  categoriesAdmin,
  categoriesClient,
  categoriesDesigner,
  categoriesLSH,
  categoriesSupplier,
  categoriesSalesperson,
  categoriesEmployee,
} from "../privatePages/layout/data/layoutCategories";

export type UserContext =
  | {
      authToken: string;
      setAuthToken: (token: string) => void;
      currentUUID: string | undefined;
      currentRoleId: number;
      isClient: boolean;
      isDesigner: boolean;
      isSupplier: boolean;
      isSalesperson: boolean;
      isUserAccessGranted: boolean;
    }
  | undefined;

interface Category {
  text: string;
  icon: JSX.Element;
  link?: string;
  subItems?: SubItem[];
}

interface SubItem {
  text: string;
  link: string;
}

export const AuthContext = createContext<UserContext>(undefined);

// TODO: check if the changes made the page load slower
export const AuthProvider = ({ children }: { children: JSX.Element }) => {
  const LOCAL_STORAGE_TOKEN = localStorage.getItem("token");
  const EXISTING_TOKEN = LOCAL_STORAGE_TOKEN && JSON.parse(LOCAL_STORAGE_TOKEN);

  const [authToken, setAuthToken] = useState(
    EXISTING_TOKEN ? EXISTING_TOKEN : ""
  );

  if (LOCAL_STORAGE_TOKEN) {
    axios.defaults.headers.common["Authorization"] = EXISTING_TOKEN;
  }

  const setToken = (token: string) => {
    localStorage.setItem("token", JSON.stringify(token));
    setAuthToken(token);
  };

  const decodedJwt: Token = authToken && jwt(authToken);

  const isClient = decodedJwt.role_id === roles.client;
  const isDesigner = decodedJwt.role_id === roles.designer;
  const isSupplier = decodedJwt.role_id === roles.supplier;
  const isSalesperson = decodedJwt.role_id === roles.salesperson;
  const currentUUID = decodedJwt?.uuid;
  const currentRoleId = decodedJwt.role_id;
  const isUserAccessGranted =
    isClient || isDesigner || isSalesperson || !currentRoleId;

  return (
    <AuthContext.Provider
      value={{
        authToken,
        setAuthToken: setToken,
        // TODO: refractor to currentUserRoleId
        currentRoleId,
        currentUUID,
        isClient,
        isDesigner,
        isSupplier,
        isSalesperson,
        isUserAccessGranted,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within a AuthProvider");
  }
  return context;
}

export type Role =
  | "client"
  | "designer"
  | "supplier"
  | "lsh"
  | "admin"
  | "salesperson"
  | "employee";

export const roles: {
  [key in Role]: number;
} = {
  client: 1,
  designer: 2,
  supplier: 3,
  lsh: 4,
  admin: 5,
  salesperson: 6,
  employee: 7,
};

export const getRoleName = (currentRoleId: number) => {
  switch (currentRoleId) {
    case roles.client:
      return "cliente";
    case roles.designer:
      return "diseñador";
    case roles.supplier:
      return "proveedor";
    case roles.lsh:
      return "lsh";
    case roles.admin:
      return "admin";
    case roles.salesperson:
      return "vendedor";
    case roles.employee:
      return "empleado";
    default:
      return "cliente";
  }
};

export const AccessLevels = {
  // any allows any user that has an account
  any: [
    roles.client,
    roles.designer,
    roles.lsh,
    roles.admin,
    roles.supplier,
    roles.salesperson,
    roles.employee,
  ],
  client: [roles.client],
  designer: [roles.designer],
  supplier: [roles.supplier],
  admin: [roles.admin],
  lsh: [roles.lsh],
  salesperson: [roles.salesperson],
  employee: [roles.employee],
  supplierTeam: [roles.supplier, roles.salesperson, roles.employee],
};

export const isLoggedIn = {
  loggedRol: JSON.parse(localStorage.getItem("user") || "{}").rol,
};

export const getCategoriesByRole = (
  roleId: number,
  companyName: string = ""
): Category[] => {
  const addCompanyNameToPaths = (categories: Category[]) => {
    return categories.map((category) => ({
      ...category,
      link: `/${companyName}${category.link}`, // Agrega companyName al principio del path
      subItems: category.subItems
        ? category.subItems.map((subItem) => ({
            ...subItem,
            link: `/${companyName}${subItem.link}`, // Agrega companyName al principio del path en subItems
          }))
        : undefined,
    }));
  };

  switch (roleId) {
    case roles.designer:
      return categoriesDesigner;
    case roles.supplier:
      return categoriesSupplier;
    case roles.lsh:
      return categoriesLSH;
    case roles.admin:
      return categoriesAdmin;
    case roles.client:
      return addCompanyNameToPaths(categoriesClient);
    case roles.salesperson:
      return categoriesSalesperson;
    case roles.employee:
      return categoriesEmployee;
    default:
      return [];
  }
};
